import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import "@/App.css";
import HomePage from "./pages/HomePage";
import UploadPage from "./pages/UploadPage";
import StyleSelectionPage from "./pages/StyleSelectionPage";
import CheckoutPage from "./pages/CheckoutPage";
import SuccessPage from "./pages/SuccessPage";
import AdminPage from "./pages/AdminPage";
import StudioPage from "./pages/StudioPage";
import SampleBookPage from "./pages/SampleBookPage";
import CreateStoryPage from "./pages/CreateStoryPage";
import CuratePage from "./pages/CuratePage";
import CuddleCreatePage from "./pages/CuddleCreatePage";
import CuddleCuratePage from "./pages/CuddleCuratePage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsPage from "./pages/TermsPage";
import RefundPolicyPage from "./pages/RefundPolicyPage";
import ShippingPolicyPage from "./pages/ShippingPolicyPage";
import BookPreviewPage from "./pages/BookPreviewPage";
import StyleRouter from "./pages/StyleRouter";
import StylePage from "./pages/StylePage";
import PromoPage from "./pages/PromoPage";
import PhotoBooksPage from "./pages/PhotoBooksPage";
import PhotoBookSuccessPage from "./pages/PhotoBookSuccessPage";
import ShopByProductPage from "./pages/ShopByProductPage";
import AuthPage from "./pages/AuthPage";
import AccountPage from "./pages/AccountPage";
import { AuthProvider } from "./contexts/AuthContext";
import { CountryProvider } from "./contexts/CountryContext";
import CountryBanner from "./components/CountryBanner";
import { Toaster } from "./components/ui/sonner";
import {
  usePageviewTracker,
  useClickTracker,
  useScrollMilestoneTracker,
  useSectionImpressionTracker,
  useSessionEndTracker,
  isAdminSession,
} from "./lib/analytics";
import { useEffect } from "react";

function AppRoutes() {
  const location = useLocation();
  usePageviewTracker();
  useClickTracker();
  useScrollMilestoneTracker();
  useSectionImpressionTracker();
  useSessionEndTracker();

  // Scroll-restoration: every time the route changes (path or search), reset
  // scroll to the top. Without this, navigating from a long homepage to
  // /upload or /checkout opens the new page mid-scroll because React keeps
  // the previous scrollY.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname, location.search]);

  useEffect(() => {
    // Touch isAdminSession once on mount so `?admin=1` in the URL flips the
    // persistent flag immediately on landing.
    isAdminSession();
  }, []);
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/upload" element={<UploadPage />} />
      <Route path="/styles" element={<StyleSelectionPage />} />
      <Route path="/checkout" element={<CheckoutPage />} />
      <Route path="/success" element={<SuccessPage />} />
      <Route path="/admin" element={<AdminPage />} />
      <Route path="/studio" element={<StudioPage />} />
      <Route path="/studio/sample-book" element={<SampleBookPage />} />
      <Route path="/cuddle-studio" element={<StudioPage vertical="cuddle" />} />
      <Route path="/create-story" element={<CreateStoryPage />} />
      <Route path="/create-story/:submissionId/:token" element={<CreateStoryPage />} />
      <Route path="/curate/:sid/:token" element={<CuratePage />} />
      <Route path="/cuddle/create" element={<CuddleCreatePage />} />
      <Route path="/cuddle/create/:submissionId/:token" element={<CuddleCreatePage />} />
      <Route path="/cuddle/curate/:sid/:token" element={<CuddleCuratePage />} />
      <Route path="/cuddle/preview/:storyId/:token" element={<BookPreviewPage />} />
      <Route path="/book-preview/:storyId/:token" element={<BookPreviewPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/refund-policy" element={<RefundPolicyPage />} />
      <Route path="/shipping-policy" element={<ShippingPolicyPage />} />
      <Route path="/promo/:slug" element={<PromoPage />} />
      <Route path="/style/:slug" element={<StylePage />} />
      <Route path="/photo-books" element={<PhotoBooksPage />} />
      <Route path="/photo-books/success" element={<PhotoBookSuccessPage />} />
      <Route path="/shop/:productType" element={<ShopByProductPage />} />
      <Route path="/login" element={<AuthPage mode="login" />} />
      <Route path="/signup" element={<AuthPage mode="signup" />} />
      <Route path="/account" element={<AccountPage />} />
      {/* Future-friendly URLs: /:collectionSlug/:styleSlug → routes into the
          existing upload flow with the right query params. Old URLs unchanged. */}
      <Route path="/:collectionSlug/:styleSlug" element={<StyleRouter />} />
      <Route path="/:collectionSlug" element={<StyleRouter />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <CountryProvider>
            <CountryBanner />
            <AppRoutes />
          </CountryProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-center" richColors theme="dark" />
    </div>
  );
}

export default App;
